import { useOutletContext, Link } from "react-router-dom";
import { useState, useEffect } from 'react';
import { Table, Input, FormGroup, Label, Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faUser as faSUser, faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import { faUser as faRUser } from '@fortawesome/free-regular-svg-icons';

import { translations } from './../../../i18/translations.js';
import AdministrationLinkRow from './assets/AdministrationLinkRow.js';
import FirstLetterSelect from './assets/FirstLetterSelect.js';
import UserDataForm from './assets/UserDataForm.js';
import Search from './../../../util/Search.js';
import Pager from './../../../util/Pager.js';
import { Space } from './../../../util/Html.js';
import './MembershipManagement.css';

const MembershipManagement = () => {
    const API_URL = process.env.REACT_APP_SERVICE_API_URL;
    const {user, lang, showAlert, checkAccess} = useOutletContext();

    const [query, setQuery] = useState('');
    const [pageIndex, setPageIndex] = useState(0);
    const [firstLetter, setFirstLetter] = useState('');
    const [onlyMember, setOnlyMember] = useState(true);
    const [pager, setPager] = useState([]);
    const [template, setTemplate] = useState('');

    const [members, setMembers] = useState([]);
    const [selectedMember, setSelectedMember] = useState(null);

    const [modalOpen, setModalOpen] = useState(false);

    const pageSize = 20;

    useEffect(() => {
        fetchMembers(pageIndex, query, firstLetter, onlyMember);
    }, [pageIndex, query, firstLetter, onlyMember, user]);

    const fetchMemberData = async (memberId) => {
        try {
            const response = await fetch(`${API_URL}/admin/get_administration_user_data.php`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({userId: memberId})
            });
            const data = await response.json();
            if (checkAccess(data)) {
                setSelectedMember(data);
                setModalOpen(true);
            }
        } catch (error) {
            console.error("Error fetching member data:", error);
        }
    };

    const fetchMembers = async (pageIndex, queryString, firstLetter, onlyMember) => {
        try {
            const payload = {
                filter: {
                    pageSize: 20,
                    pageIndex: pageIndex,
                    queryString: queryString,
                    firstLetter: firstLetter,
                    onlyMember: onlyMember
                }
            };

            const response = await fetch(`${API_URL}/admin/get_administration_user_list.php`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (checkAccess(data)) {
                setMembers(data.data);
                setPager(data.pagerButtonIndexes);
                setTemplate(data.template);
            }
        } catch (error) {
            console.error("Error fetching members:", error);
        }
    };

    const onSave = async (baseData) => {
        try {
            const payload = {
                userData: baseData
            };

            const response = await fetch(`${API_URL}/admin/save_user.php`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (checkAccess(data)) {
                showAlert(data.alert);
                await fetchMembers(0, "", firstLetter, onlyMember);
                await fetchMemberData(data.userId);
            }
        } catch (error) {
            console.error("Error fetching members:", error);
        }

    };

    const onClose = () => {
        setModalOpen(false);
    };

    const handleRowClick = (memberId) => {
        fetchMemberData(memberId);
    };

    const handleFirstLetterChange = (c) => {
        setFirstLetter(c);
        setPageIndex(0);
    };

    return (
            <div className="mbkegy-content">
                <h1 className="mbkegy-content">{translations[lang].membership_management}</h1>
                <AdministrationLinkRow lang={lang} />
            
                <div className="membership-management-filter-container mb-3">
                    <Search placeholder={translations[lang].search_member} handleSearch={setQuery} />
                    <Button className="text-nowrap" color="success" title={translations[lang].anew} onClick={() => handleRowClick(-1)}>
                        <FontAwesomeIcon icon={faUserPlus} />
                    </Button>
                </div>
            
                <div className="membership-management-filter-container mb-3">
                    <FirstLetterSelect onlyMember={onlyMember} firstLetter={firstLetter} handleSelectionChange={handleFirstLetterChange} />
                    <FormGroup switch className="ms-auto">
                        <Input id="onlyMembersInUserList" type="switch" checked={onlyMember} onClick={() => setOnlyMember(!onlyMember)} />
                        <Label for="onlyMembersInUserList" check>{translations[lang].only_members}</Label>
                    </FormGroup>
                </div>
            
                <Table striped className="mbkegy-table">
                    <thead>
                        <tr>
                            <th style={{width: '35%'}}>{translations[lang].name}</th>
                            <th style={{width: '65%'}}>{translations[lang].institute}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {members && members.map((member) => (
                                <tr key={member.id} onClick={() => handleRowClick(member.id)} style={{cursor: "pointer"}}>
                                    <td>
                                        {member.fullname ? (
                                                        <>
                                            <FontAwesomeIcon
                                                icon={member.memberId === null ? faRUser : faSUser}
                                                />{" "}
                                            {member.fullname}
                                            </>
                                                    ) : (
                                                        <Space />
                                                    )}
                            </td>
                            <td>{member.institute || <Space />}</td>
                            </tr>
                                    ))}
                    </tbody>
                </Table>
            
                <Pager template={`${translations[lang].rows}: ${template}`} pager={pager} handlePageChange={setPageIndex} />
            
                {/* User editor dialog */}
                {selectedMember && (
                            <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} size="lg">
                                <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
                                    <h1 className="mbkegy-content">
                                        {selectedMember.baseData && selectedMember.baseData.id !== '-1' ? selectedMember.baseData.fullname : translations[lang].new_user}
                                    </h1>
                                </ModalHeader>
                                <ModalBody>
                                    <UserDataForm selectedMember={selectedMember} onSave={onSave} onClose={onClose} fetchMemberData={fetchMemberData} adminMode={true}/>
                                </ModalBody>
                            </Modal>
                            )}
            </div>
            );
};

export default MembershipManagement;
